import { buildOrderPayload as buildOrderPayloadFromUtils } from '@Services/utils/builder/orderPayloadBuilder';
import React, { createContext, useCallback, useContext, useMemo, useReducer } from 'react';
import { OrderContact, OrderPayload } from 'src/@types/customerPortalApi';
import { GroupedService as NewGroupedService } from '@Services/types/service.types';
import {
  resetSelections as resetSelectionsAction,
  resetStep as resetStepAction,
  setComment as setCommentAction,
  setCurrentStep as setCurrentStepAction,
  setEarliestPossible as setEarliestPossibleAction,
  setSelectedDate as setSelectedDateAction,
  setServiceMetadata as setServiceMetadataAction,
  updateServiceQuantity as updateServiceQuantityAction,
} from './ServicesActions';
import { initialServicesState, servicesReducer } from './ServicesReducer';

export type ServiceStep = 'selection' | 'checkout' | 'confirmation';

/**
 * Services context interface.
 * Follows portal-app's clean pattern: selections + metadata only.
 */
interface ServicesContextType {
  /** Update the selected quantity for a grouped service */
  updateServiceQuantity: (rowId: string, quantity: number) => void;
  /** Set service metadata (called when data is loaded) */
  setServiceMetadata: (services: NewGroupedService[]) => void;
  /** Get service metadata by rowId */
  getServiceMetadata: (rowId: string) => NewGroupedService | undefined;
  /** Get current selections (rowId -> quantity) */
  selections: Record<string, number>;
  /** Items currently in the cart (selectedQuantity > 0) */
  cartItems: NewGroupedService[];
  /** Total count of all selected services (sum of all selectedQuantity values) */
  totalSelectedQuantity: number;
  /** Build order payload for backend submission */
  buildOrderPayload: (params: {
    date: string | null;
    contact: OrderContact;
    loadingInformation: string;
  }) => OrderPayload;
  /** Reset all selected quantities to 0 */
  resetSelections: () => void;
  /** Set earliest possible timestamp (for "Snarast möjligast") */
  setEarliestPossible: (timestamp: number | null) => void;
  /** Set selected date (for calendar date selection) */
  setSelectedDate: (date: string | null) => void;
  /** Set the comment/message from the user */
  setComment: (comment: string) => void;
  /** Timestamp when "Snarast möjligast" is selected, null otherwise */
  earliestPossible: number | null;
  /** Selected date in YYYY-MM-DD format, null otherwise */
  selectedDate: string | null;
  /** Optional comment/message from the user */
  comment: string;

  /** Current step in the order flow */
  currentStep: ServiceStep;
  /** Set current step */
  setCurrentStep: (step: ServiceStep) => void;
  /** Reset step */
  resetStep: () => void;
}

const ServicesContext = createContext<ServicesContextType | undefined>(undefined);

export function ServicesProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(servicesReducer, initialServicesState);

  const setServiceMetadata = useCallback((services: NewGroupedService[]) => {
    dispatch(setServiceMetadataAction(services));
  }, []);

  const getServiceMetadata = useCallback(
    (rowId: string): NewGroupedService | undefined => {
      return state.serviceMetadata[rowId];
    },
    [state.serviceMetadata]
  );

  const updateServiceQuantity = useCallback(
    (rowId: string, quantity: number) => {
      const metadata = state.serviceMetadata[rowId];
      if (metadata) {
        quantity = Math.max(0, Math.min(quantity, metadata.maxQuantity));
      } else {
        quantity = Math.max(0, quantity);
      }

      dispatch(updateServiceQuantityAction(rowId, quantity));

      if (import.meta.env.MODE === 'development') {
        // eslint-disable-next-line no-console
        console.log(
          '[Service Selection]',
          JSON.stringify(
            {
              metadata: metadata ? { ...metadata, selectedQuantity: quantity } : null,
            },
            null,
            2
          )
        );
      }
    },
    [state.serviceMetadata]
  );

  const cartItems = useMemo(() => {
    return Object.entries(state.selections)
      .filter(([_, quantity]) => quantity > 0)
      .map(([rowId, quantity]) => {
        const metadata = state.serviceMetadata[rowId];
        if (!metadata) {
          return null;
        }
        return { ...metadata, selectedQuantity: quantity };
      })
      .filter((item): item is NewGroupedService => item !== null);
  }, [state.selections, state.serviceMetadata]);

  const resetSelections = useCallback(() => {
    dispatch(resetSelectionsAction());
  }, []);

  const setEarliestPossible = useCallback((timestamp: number | null) => {
    dispatch(setEarliestPossibleAction(timestamp));
  }, []);

  const setSelectedDate = useCallback((date: string | null) => {
    dispatch(setSelectedDateAction(date));
  }, []);

  const setComment = useCallback((comment: string) => {
    dispatch(setCommentAction(comment));
  }, []);

  const totalSelectedQuantity = useMemo(() => {
    return Object.values(state.selections).reduce((total, quantity) => total + quantity, 0);
  }, [state.selections]);

  const buildOrderPayload = useCallback(
    (params: {
      date: string | null;
      contact: OrderContact;
      loadingInformation: string;
    }): OrderPayload => {
      return buildOrderPayloadFromUtils(
        cartItems,
        params.date,
        params.contact,
        params.loadingInformation
      );
    },
    [cartItems]
  );

  const setCurrentStep = useCallback((step: ServiceStep) => {
    dispatch(setCurrentStepAction(step));
  }, []);

  const resetStep = useCallback(() => {
    dispatch(resetStepAction());
  }, []);

  const value = useMemo(
    () => ({
      updateServiceQuantity,
      setServiceMetadata,
      getServiceMetadata,
      selections: state.selections,
      cartItems,
      totalSelectedQuantity,
      buildOrderPayload,
      resetSelections,
      setEarliestPossible,
      setSelectedDate,
      setComment,
      earliestPossible: state.earliestPossible,
      selectedDate: state.selectedDate,
      comment: state.comment,
      currentStep: state.currentStep,
      setCurrentStep,
      resetStep,
    }),
    [
      updateServiceQuantity,
      setServiceMetadata,
      getServiceMetadata,
      state.selections,
      cartItems,
      totalSelectedQuantity,
      buildOrderPayload,
      resetSelections,
      setEarliestPossible,
      setSelectedDate,
      setComment,
      state.earliestPossible,
      state.selectedDate,
      state.comment,
      state.currentStep,
      setCurrentStep,
      resetStep,
    ]
  );

  return <ServicesContext.Provider value={value}>{children}</ServicesContext.Provider>;
}

export function useServicesContext() {
  const context = useContext(ServicesContext);
  if (context === undefined) {
    throw new Error('useServicesContext must be used within a ServicesProvider');
  }
  return context;
}
